import type React from "react";
import { useEffect, useState } from "react";
import {
  type EditableFileOps,
  useEditableAttachments,
} from "@/components/common/hooks/useEditableAttachments";
import type { PostChannelMoveOption } from "@/features/study-group-stream/types/postChannelMove";
import { useConfirmDeleteDialog } from "@/hooks/useConfirmDeleteDialog";
import type { ReactionType } from "@/lib/humhub-api/types";

export type PostFileOps = EditableFileOps;

interface UsePostCardActionsOptions {
  content: string;
  postId: number;
  currentReaction: ReactionType | null;
  isPinned: boolean;
  isEditing: boolean;
  isDeleting: boolean;
  onReaction?: (reaction: ReactionType | null) => Promise<void>;
  onPin?: (isPinned: boolean) => Promise<void>;
  onReport?: (reason: number, details: string) => Promise<void>;
  onEdit?: (
    postId: number,
    newMessage: string,
    fileOps?: PostFileOps,
  ) => Promise<void>;
  onDelete?: (postId: number) => Promise<void>;
  onCommentToggle?: () => void;
  onMoveChannel?: (channel: PostChannelMoveOption) => Promise<void>;
  movableChannels: PostChannelMoveOption[];
  canMoveChannel: boolean;
  onReportSuccess?: () => void;
  onReportError?: () => void;
  onEditValidationError?: () => void;
  onEditError?: () => void;
  onDeleteError?: () => void;
}

export const usePostCardActions = ({
  content,
  postId,
  currentReaction,
  isPinned,
  isEditing,
  isDeleting,
  onReaction,
  onPin,
  onReport,
  onEdit,
  onDelete,
  onCommentToggle,
  onMoveChannel,
  movableChannels,
  canMoveChannel,
  onReportSuccess,
  onReportError,
  onEditValidationError,
  onEditError,
  onDeleteError,
}: UsePostCardActionsOptions) => {
  const [localReaction, setLocalReaction] = useState<ReactionType | null>(
    currentReaction,
  );
  const [localPinned, setLocalPinned] = useState(isPinned);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedMessage, setEditedMessage] = useState(content);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [isDeletingPost, setIsDeletingPost] = useState(false);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [selectedMoveChannelId, setSelectedMoveChannelId] = useState<
    string | undefined
  >(undefined);
  const deleteDialog = useConfirmDeleteDialog();

  const attachments = useEditableAttachments();
  const { filesToRemove, filesToReplace, filesToAdd, addFilesInputRef } =
    attachments;

  useEffect(() => {
    setLocalReaction(currentReaction);
  }, [currentReaction]);

  useEffect(() => {
    setLocalPinned(isPinned);
  }, [isPinned]);

  useEffect(() => {
    setEditedMessage(content);
  }, [content]);

  useEffect(() => {
    if (!isEditing) {
      setIsEditMode(false);
    }
  }, [isEditing]);

  useEffect(() => {
    if (!isMoveDialogOpen) return;
    if (movableChannels.length === 0) {
      setSelectedMoveChannelId(undefined);
      return;
    }

    if (!selectedMoveChannelId) {
      setSelectedMoveChannelId(movableChannels[0].id);
      return;
    }

    const stillExists = movableChannels.some(
      (channel) => channel.id === selectedMoveChannelId,
    );
    if (!stillExists) {
      setSelectedMoveChannelId(movableChannels[0].id);
    }
  }, [isMoveDialogOpen, movableChannels, selectedMoveChannelId]);

  const handleReactionSelect = async (reaction: ReactionType) => {
    const newReaction = localReaction === reaction ? null : reaction;
    setLocalReaction(newReaction);

    try {
      await onReaction?.(newReaction);
    } catch (error) {
      setLocalReaction(currentReaction);
      console.error("Reaction failed:", error);
    }
  };

  const handlePinPost = async () => {
    if (!onPin) return;

    const newPinnedState = !localPinned;
    setLocalPinned(newPinnedState);

    try {
      await onPin(newPinnedState);
    } catch (error) {
      setLocalPinned(!newPinnedState);
      console.error("Failed to pin/unpin post:", error);
    }
  };

  const handleReportSubmit = async (reason: number, details: string) => {
    if (!onReport) {
      console.error("onReport function is not provided");
      return;
    }

    setIsSubmittingReport(true);

    try {
      await onReport(reason, details);
      setShowReportDialog(false);
      onReportSuccess?.();
    } catch (error) {
      console.error("Failed to submit report:", error);
      onReportError?.();
    } finally {
      setIsSubmittingReport(false);
    }
  };

  const handleEditClick = () => {
    setIsEditMode(true);
    setEditedMessage(content);
  };

  const handleRemoveExistingFile = attachments.removeExistingFile;
  const handleReplaceExistingFile = attachments.replaceExistingFile;
  const handleRemoveNewFile = attachments.removeNewFile;
  const handleAddFilesInputChange = attachments.addFilesInputChange;

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedMessage(content);
    attachments.reset();
  };

  const handleSubmitEdit = async (submittedMessage?: string) => {
    const trimmedMessage = (submittedMessage ?? editedMessage).trim();
    const hasFileChanges =
      filesToRemove.size > 0 ||
      filesToReplace.size > 0 ||
      filesToAdd.length > 0;

    if (!onEdit) {
      setIsEditMode(false);
      return;
    }

    if (!hasFileChanges && trimmedMessage === content.trim()) {
      setIsEditMode(false);
      return;
    }

    if (trimmedMessage === "") {
      onEditValidationError?.();
      return;
    }

    const fileOps = attachments.fileOps;

    setIsEditMode(false);
    setEditedMessage(trimmedMessage);
    setIsSubmittingEdit(true);

    try {
      await onEdit(postId, trimmedMessage, fileOps);
    } catch (error) {
      console.error("Failed to edit post:", error);
      onEditError?.();
      setEditedMessage(content);
      setIsEditMode(true);
    } finally {
      setIsSubmittingEdit(false);
      attachments.reset();
    }
  };

  const handleDeleteClick = async () => {
    if (!onDelete) return;
    deleteDialog.open();
  };

  const handleDeleteConfirm = async () => {
    if (!onDelete) return;

    setIsDeletingPost(true);

    try {
      await onDelete(postId);
    } catch (error) {
      console.error("Failed to delete post:", error);
      onDeleteError?.();
    } finally {
      setIsDeletingPost(false);
      deleteDialog.close();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey) {
      void handleSubmitEdit();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  const handleCommentClick = () => {
    onCommentToggle?.();
  };

  const handleMoveChannelClick = () => {
    if (!canMoveChannel) return;
    setSelectedMoveChannelId(movableChannels[0]?.id);
    setIsMoveDialogOpen(true);
  };

  const handleSubmitMoveChannel = async () => {
    if (!onMoveChannel || !selectedMoveChannelId) return;

    const selectedChannel = movableChannels.find(
      (channel) => channel.id === selectedMoveChannelId,
    );
    if (!selectedChannel) return;

    try {
      await onMoveChannel(selectedChannel);
      setIsMoveDialogOpen(false);
    } catch (error) {
      console.error("Failed to move post channel:", error);
    }
  };

  const isPostDeleting = isDeleting || isDeletingPost;

  return {
    localReaction,
    localPinned,
    showReportDialog,
    isSubmittingReport,
    isEditMode,
    editedMessage,
    setEditedMessage,
    isSubmittingEdit,
    isPostDeleting,
    isMoveDialogOpen,
    setIsMoveDialogOpen,
    selectedMoveChannelId,
    setSelectedMoveChannelId,
    deleteDialog,
    filesToRemove,
    filesToReplace,
    filesToAdd,
    addFilesInputRef,
    handleReactionSelect,
    handlePinPost,
    handleReportSubmit,
    setShowReportDialog,
    handleEditClick,
    handleCancelEdit,
    handleSubmitEdit,
    handleDeleteClick,
    handleDeleteConfirm,
    handleKeyDown,
    handleCommentClick,
    handleMoveChannelClick,
    handleSubmitMoveChannel,
    handleRemoveExistingFile,
    handleReplaceExistingFile,
    handleRemoveNewFile,
    handleAddFilesInputChange,
  };
};
